import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  FilterList,
  Insights,
  InsightsOutlined,
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  WatchLater,
  WatchLaterOutlined,
} from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { INFORMATIONAL_PROXY_EVENT_TYPE_OPTIONS } from "src/constants/options/proxyEventOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useProxyEventsQuery } from "src/store/proxyEvent";
import { getColumnData, getColumns, getRowData } from "./utils";
import { IProxyEvent } from "src/store/proxyEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { PROXY_NON_VOTING_TYPES } from "src/constants/taskTypes";

export type InformationalProxyEventsView =
  | "upcoming"
  | "active"
  | "recently-completed"
  | "historical";

export const INFORMATIONAL_PROXY_EVENTS_VIEW: {
  [key: string]: InformationalProxyEventsView;
} = {
  UPCOMING: "upcoming",
  ACTIVE: "active",
  RECENTLY_COMPLETED: "recently-completed",
  HISTORICAL: "historical",
};

export function InformationalProxyEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    INFORMATIONAL_PROXY_EVENTS_VIEW.UPCOMING) as InformationalProxyEventsView;
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useProxyEventsQuery({
    offset,
    limit,
    ordering,
    types: type ? [type] : Object.keys(PROXY_NON_VOTING_TYPES),
    tab: view || INFORMATIONAL_PROXY_EVENTS_VIEW.UPCOMING,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IProxyEvent) => {
    const { id } = event;
    navigate(`/proxy-events/${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(() => {
    return getColumnData(view, {
      getSort,
      onOrderingChange,
    });
  }, [view, getSort, onOrderingChange]);
  const rowData = getRowData(view, data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={INFORMATIONAL_PROXY_EVENT_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view}
      />
    </FormProvider>
  );

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Stack>
        <SecondaryTabs
          sx={{ mb: 8, mr: "auto" }}
          tabs={[
            {
              label: "Upcoming",
              value: INFORMATIONAL_PROXY_EVENTS_VIEW.UPCOMING,
              icon:
                view === INFORMATIONAL_PROXY_EVENTS_VIEW.UPCOMING ? (
                  <WatchLater style={{ color: "#1968FF" }} />
                ) : (
                  <WatchLaterOutlined />
                ),
            },
            {
              label: "Active",
              value: INFORMATIONAL_PROXY_EVENTS_VIEW.ACTIVE,
              icon:
                view === INFORMATIONAL_PROXY_EVENTS_VIEW.ACTIVE ? (
                  <Insights style={{ color: "#1968FF" }} />
                ) : (
                  <InsightsOutlined />
                ),
            },
            {
              label: "Recently completed",
              value: INFORMATIONAL_PROXY_EVENTS_VIEW.RECENTLY_COMPLETED,
              icon:
                view === INFORMATIONAL_PROXY_EVENTS_VIEW.RECENTLY_COMPLETED ? (
                  <Verified style={{ color: "#1968FF" }} />
                ) : (
                  <VerifiedOutlined />
                ),
            },
            {
              label: "Historical",
              value: INFORMATIONAL_PROXY_EVENTS_VIEW.HISTORICAL,
              icon:
                view === INFORMATIONAL_PROXY_EVENTS_VIEW.HISTORICAL ? (
                  <History style={{ color: "#1968FF" }} />
                ) : (
                  <HistoryOutlined />
                ),
            },
          ]}
          activeTab={view}
          clickHandler={handleSecondaryTabChange}
        />
      </Stack>
      <Table
        count={data?.count || 0}
        columnData={columnData}
        columns={columns}
        isFetching={isFetching}
        isLoading={isLoading}
        noResultsData={{
          title: "No Informational Proxy Events found",
          subtitle:
            "Sorry, there are no Informational Proxy Events that match your applied filters.",
        }}
        paginateProps={paginateProps}
        rows={rowData}
        tableMinWidth={1100}
        title={
          view === INFORMATIONAL_PROXY_EVENTS_VIEW.UPCOMING
            ? "Upcoming Informational Proxy Events"
            : view === INFORMATIONAL_PROXY_EVENTS_VIEW.ACTIVE
            ? "Active Informational Proxy Events"
            : view === INFORMATIONAL_PROXY_EVENTS_VIEW.RECENTLY_COMPLETED
            ? "Recently Completed Informational Proxy Events"
            : "Historical Informational Proxy Events"
        }
        titleVariant="subtitle2"
        toolbarInside
        toolbarCollapse={{
          Icon: FilterList,
          collapseContent: filterList,
          isFlat: true,
        }}
      />
    </ScrollContainer>
  );
}
